// v4 port of v1's DemoHeader (src/components/demo) — same gate, same
// DEMO_USERS source, same switch_identity wire. Deferred out of AppShell's
// Phase 1 omissions (see src/MIGRATION.md § v4 shell Phase-1 omissions);
// this is its migration. Mounted in AppShell above the Outlet so it renders
// on BOTH migrated and v1-fallback routes — same placement decision as the
// mobile sidebar-trigger bar directly above it.
import { useDemoResetCountdown } from '@/components/ap/layout/demoSwitcher/useDemoResetCountdown';
import { useEnabledFeatures } from '@/hooks/dataHelpers';
import { ClockCircleLinear, UserRoundedLinear } from '@assessprep/icons/solar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DemoAccountsDialog } from './DemoAccountsDialog';

// `demoBuild` comes from the composing shell (isDemoBuild()) — the import
// law keeps config wiring out of components/ap.
export const DemoBar = ({ demoBuild }: { demoBuild: boolean }) => {
  const { t } = useTranslation();
  const enabledFeatures = useEnabledFeatures();
  // Bar/switcher gate, unchanged (broad): real demo env OR the org-level flag.
  // Timer + reset navigation run ONLY on a true demo build — same predicate
  // DemoMaintenancePage stays on, so the maintenance ping-pong can't occur.
  const isDemoMode = demoBuild;
  const isDemoSurface = isDemoMode || !!enabledFeatures.show_demo_account_switcher;
  const [dialogOpen, setDialogOpen] = useState(false);
  const countdown = useDemoResetCountdown(isDemoMode);

  if (!isDemoSurface) return null;

  return (
    <div
      className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-4 text-foreground"
      data-testid="v4-demo-bar"
    >
      {isDemoMode && (
        <Badge
          variant="outline"
          className="gap-1.5 border-ap-warning/30 bg-ap-warning-a15 text-ap-warning"
          data-testid="v4-demo-bar-countdown"
          aria-label={`${t('V4Shell.demo_resets_in')} ${countdown}`}
        >
          <ClockCircleLinear aria-hidden="true" className="size-3.5" />
          <span aria-hidden="true">{t('V4Shell.demo_resets_in')}</span>
          <span aria-hidden="true" className="tabular-nums">
            {countdown}
          </span>
        </Badge>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ms-auto"
        onClick={() => setDialogOpen(true)}
        data-testid="v4-demo-bar-switch-trigger"
      >
        <UserRoundedLinear aria-hidden="true" />
        {t('V4Shell.demo_switch_user')}
      </Button>
      <DemoAccountsDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};
